/**
 * Returns the most appropriate data and loading state from an Apollo query
 * during cache key transitions (e.g., variable changes after auth resolves).
 *
 * **Problem**: When Apollo query variables change (e.g., `isAdmin` flips after GetMe),
 * Apollo treats it as a new cache key. The new key has no cached data, so `data` becomes
 * `undefined` and `loading` oscillates while the network request is in flight. This causes
 * UI flashes even though we have perfectly good stale data to show.
 *
 * **Solution**: This hook resolves two things:
 * 1. **effectiveData** — falls back to `previousData` when `data` is undefined during loading,
 *    keeping the UI stable. Once the new response arrives, always returns latest `data`.
 * 2. **loading** — reports `false` whenever `effectiveData` has a value, even if Apollo's
 *    internal `loading` is `true` (e.g., background refetch via `cache-and-network`).
 *    Only reports `true` when there's genuinely no data to display.
 *
 * @example
 * const { data, previousData, loading } = useSomeQuery({ variables: { isAdmin } });
 * const { effectiveData, loading: effectiveLoading } = useEffectiveGqlCacheData({ data, previousData, loading });
 */
export const useEffectiveGqlCacheData = <TData>(
  args: Readonly<{ data: TData | undefined; previousData: TData | undefined; loading: boolean }>,
): Readonly<{ effectiveData: TData | undefined; loading: boolean }> => {
  const { data, previousData, loading } = args;

  const effectiveData = loading && data === undefined ? previousData : data;

  return {
    effectiveData,
    // Only report loading when we have nothing to show.
    // If effectiveData exists (from cache or previousData), the UI can render — no spinner needed.
    loading: loading && effectiveData === undefined,
  };
};
